import React, { useEffect, useState } from 'react'
import { Button, Card, Col, DatePicker, Form, Input, Row, Select, Spin, Table } from 'antd'
import { FileExcelOutlined, FilePdfOutlined } from '@ant-design/icons'
import * as XLSX from 'xlsx'
import BaseListing from 'src/BaseListings'
import { wtApiCall } from 'src/helpers/commons/ApiWrapper'
import { wtGetFromLocalStorage } from 'src/helpers/commons/LocalStorage'
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications'
import WidgetBranchSelector from 'src/widgets/Selectors/WidgetBranchSelector'
import WidgetDepartmentSelector from 'src/widgets/Selectors/WidgetDepartmentSelector'
import {
  INTERNAL_REQUISITION_STATUS_DROPDOWN_OPTIONS,
  INTERNAL_REQUISITION_STATUS_NAME,
} from 'src/models/internal-requisition'
import Pdf from './Pdf'

const safeName = (value) => value?.name || ''
const formatDateOnly = (value) => (value ? String(value).split(/[ T]/)[0] : '')

const InternalRequisitionReport = () => {
  const [form] = Form.useForm()
  const Base = new BaseListing('internal_requisition_report')
  const [isLoading, setIsLoading] = useState(false)
  const [reportData, setReportData] = useState([])
  const [dateRange, setDateRange] = useState([])
  const [branchId, setBranchId] = useState(0)
  const [branchName, setBranchName] = useState('')
  const [departmentId, setDepartmentId] = useState(0)
  const [departmentName, setDepartmentName] = useState('')
  const [branches] = useState(wtGetFromLocalStorage('branches') || [])
  const [departments, setDepartments] = useState([])

  useEffect(() => {
    Base.dropdownData('departments', {}, [setDepartments], 1)
  }, [])

  const clearReport = () => setReportData([])

  const fetchReport = () => {
    if (!dateRange || dateRange.length !== 2) {
      lzNotifyErrorMessageFromResult('Please select requisition date range')
      return
    }

    setIsLoading(true)
    wtApiCall(
      'reports/internal_requisition_report',
      {
        from_date: dateRange[0].format('YYYY-MM-DD'),
        to_date: dateRange[1].format('YYYY-MM-DD'),
        branch_id: branchId,
        department_id: departmentId,
        code: form.getFieldValue('code') || '',
        item_name: form.getFieldValue('item_name') || '',
        status: form.getFieldValue('status') || [],
      },
      'get',
      (response) => {
        setReportData(response?.data || [])
        setIsLoading(false)
      },
      (error) => {
        lzNotifyErrorMessageFromResult(error)
        setIsLoading(false)
      },
    )
  }

  const exportReport = () => {
    const rows = reportData.map((row, index) => ({
      SR: index + 1,
      'IR Code': row.internal_requisition?.code || '',
      'IR Date': formatDateOnly(row.internal_requisition?.created_at),
      Department: safeName(row.internal_requisition?.department),
      Project: safeName(row.internal_requisition?.project),
      Status: INTERNAL_REQUISITION_STATUS_NAME[row.internal_requisition?.status] || '',
      Item: safeName(row.item),
      Unit: safeName(row.item_unit),
      Quantity: row.quantity,
      'Required Date': row.required_date || '',
      Remarks: row.remarks || '',
      'Created By Name': safeName(row.internal_requisition?.created_by),
    }))
    const worksheet = XLSX.utils.json_to_sheet(rows)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Requisitions')
    XLSX.writeFile(workbook, 'internal_requisition_report.xlsx')
  }

  const printReport = () => {
    const dateLabel = dateRange.length === 2
      ? `${dateRange[0].format('YYYY-MM-DD')} to ${dateRange[1].format('YYYY-MM-DD')}`
      : ''
    Pdf(reportData, { dateLabel })
  }

  const columns = [
    { title: 'SR', width: 55, fixed: 'left', render: (_, __, index) => index + 1 },
    {
      title: 'IR Code',
      width: 130,
      fixed: 'left',
      render: (_, row) => (
        <a href={`/internal_requisition#view:${row.internal_requisition?.id}`} target="_blank" rel="noreferrer">
          {row.internal_requisition?.code || ''}
        </a>
      ),
    },
    { title: 'IR Date', width: 115, render: (_, row) => formatDateOnly(row.internal_requisition?.created_at) },
    { title: 'Department', width: 170, render: (_, row) => safeName(row.internal_requisition?.department) },
    { title: 'Project', width: 170, render: (_, row) => safeName(row.internal_requisition?.project) },
    {
      title: 'Status',
      width: 110,
      render: (_, row) => INTERNAL_REQUISITION_STATUS_NAME[row.internal_requisition?.status] || '',
    },
    { title: 'Item', width: 220, render: (_, row) => safeName(row.item) },
    { title: 'Unit', width: 100, render: (_, row) => safeName(row.item_unit) },
    { title: 'Quantity', dataIndex: 'quantity', width: 100, align: 'right' },
    { title: 'Required Date', dataIndex: 'required_date', width: 125 },
    { title: 'Remarks', dataIndex: 'remarks', width: 240 },
    { title: 'Created By Name', width: 160, render: (_, row) => safeName(row.internal_requisition?.created_by) },
  ]

  return (
    <div className="container-fluid" style={{ padding: '1em', backgroundColor: 'white', minHeight: '100vh' }}>
      <Card title="Internal Requisition Report">
        <Form form={form} layout="vertical" onFinish={fetchReport}>
          <Row gutter={[16, 8]}>
            <Col xs={24} md={5}>
              <Form.Item label="Requisition Date Range" required>
                <DatePicker.RangePicker value={dateRange} onChange={(value) => { setDateRange(value || []); clearReport() }} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={4}>
              <Form.Item label="Branch">
                <WidgetBranchSelector value={branchId} displayValue={branchName} branches={branches} showCloudIcon={false} onChange={(value) => { setBranchId(value?.value || 0); setBranchName(value?.label || ''); clearReport() }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={4}>
              <Form.Item label="Department">
                <WidgetDepartmentSelector value={departmentId} displayValue={departmentName} departments={departments} onChange={(value) => { setDepartmentId(value?.value || 0); setDepartmentName(value?.label || ''); clearReport() }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={3}>
              <Form.Item name="code" label="IR Code"><Input placeholder="Search code" /></Form.Item>
            </Col>
            <Col xs={24} md={4}>
              <Form.Item name="item_name" label="Item"><Input placeholder="Search item" /></Form.Item>
            </Col>
            <Col xs={24} md={4}>
              <Form.Item name="status" label="Status"><Select mode="multiple" allowClear options={INTERNAL_REQUISITION_STATUS_DROPDOWN_OPTIONS} placeholder="All statuses" /></Form.Item>
            </Col>
          </Row>
          <Button type="primary" htmlType="submit" loading={isLoading}>Search</Button>
          <Button icon={<FileExcelOutlined />} onClick={exportReport} disabled={!reportData.length} style={{ marginLeft: 8 }}>Export</Button>
          <Button icon={<FilePdfOutlined />} onClick={printReport} disabled={!reportData.length} style={{ marginLeft: 8 }}>Print PDF</Button>
        </Form>
      </Card>
      <Card style={{ marginTop: 20 }}>
        <Spin spinning={isLoading}>
          <Table columns={columns} dataSource={reportData} rowKey="id" pagination={false} bordered scroll={{ x: 1800, y: 520 }} />
        </Spin>
      </Card>
    </div>
  )
}

export default InternalRequisitionReport
